import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';

interface WaitlistModalProps {
    isOpen: boolean;
    onClose: () => void;
    // Pre-fills the email field — e.g. when a visitor already typed it into
    // AuthModal before signup was gated, so they don't have to retype it.
    initialEmail?: string;
}

export const WaitlistModal = ({ isOpen, onClose, initialEmail = '' }: WaitlistModalProps) => {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState<string>(initialEmail);

    // `isOpen` toggles a `return null` inside this same component instance
    // rather than mounting/unmounting it, so useState's initializer only
    // ever runs once — sync it explicitly whenever the modal opens.
    useEffect(() => {
        if (isOpen) {
            setEmail(initialEmail);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSubmit = async () => {
        setError('');
        setIsLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/waitlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, language: i18n.language }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 500) {
                    setError(t('waitlist.errorExists'));
                } else {
                    setError(data.error || t('waitlist.errorGeneric'));
                }
                return;
            }

            setIsSubmitted(true);
        } catch (err) {
            setError(t('waitlist.errorConnection'));
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black transition-opacity duration-500 ease-in-out flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
            <div
                className={`bg-white p-8 rounded-lg shadow-xl w-96 transform transition-all duration-500 ease-out
          ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
            >
                {!isSubmitted ? (
                    <div className="animate-fadeIn">
                        <h2 className="text-xl font-bold mb-4" style={{ color: '#00c4cc' }}>
                            {t('waitlist.title')}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {t('waitlist.text')}
                        </p>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}>
                            <div className="space-y-2">
                                <Input
                                    type="email"
                                    placeholder={t('auth.emailPlaceholder')}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setError('');
                                    }}
                                    className={`w-full ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
                                    disabled={isLoading}
                                />
                                {error && (
                                    <p className="text-red-500 text-sm animate-fadeIn">
                                        {error}
                                    </p>
                                )}
                            </div>
                            <Button
                                type="submit"
                                className="w-full text-white mt-6 mb-4 transform transition-transform duration-300 hover:scale-102"
                                style={{ backgroundColor: '#00c4cc' }}
                                disabled={isLoading || !email}
                            >
                                {isLoading ? (
                                    <span className="flex items-center justify-center">
                                        <span className="mr-2">{t('waitlist.joining')}</span>
                                    </span>
                                ) : (
                                    t('waitlist.join')
                                )}
                            </Button>
                        </form>
                    </div>
                ) : (
                    <div className="animate-fadeIn">
                        <h2 className="text-xl font-bold mb-4" style={{ color: '#00c4cc' }}>
                            {t('waitlist.successTitle')}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {t('waitlist.successText')}
                        </p>
                    </div>
                )}
                <Button
                    onClick={onClose}
                    className="w-full transform transition-transform duration-300 hover:scale-102"
                    variant="outline"
                    disabled={isLoading}
                >
                    {t('waitlist.close')}
                </Button>
            </div>
        </div>
    );
};
